import {
  Button,
  JoonUIColor,
  ModalHeader,
  TextButton,
  Typography,
} from "@joonapp/web-shared"
import dayjs, { Dayjs } from "dayjs"

import DeleteScheduledMessageModal, {
  useDeleteScheduledMessageModalStore,
} from "./DeleteScheduledMessageModal"
import useScheduledMessageModalStore, {
  ScheduledMessageModalView,
} from "./useScheduledMessageModalStore"
import AddAttachmentButton from "../../../components/attachments/AddAttachmentButton"
import AttachmentItem from "../../../components/attachments/AttachmentItem"
import DateInput from "../../../components/dateInput/DateInput"
import CustomTextArea from "../../../components/textarea/TextArea"
import TimeInputNew from "../../../components/timeInput/TimeInputNew"
import { useThrottle } from "../../../hooks/useThrottle"
import useUserRole from "../../../hooks/useUserRole"
import { createResource } from "../../../networking/resources/api"
import {
  useCreateScheduledMessageMutation,
  useUpdateScheduledMessageMutation,
} from "../../../networking/scheduledMessages/mutations"
import { useScheduledMessagesQuery } from "../../../networking/scheduledMessages/queries"
import { ANALYTIC_EVENTS } from "../../../util/analytics"
import { trackAnalyticEvent } from "../../../util/analytics"
import { updateDateInDatetime, updateTimeInDatetime } from "../../../util/time"
import useCurrentClient from "../hooks/useCurrentClient"

const AddEditScheduledMessage = () => {
  const {
    onClose,
    message,
    setMessage,
    view,
    scheduledMessageId,
    clientDatetime,
    setClientDatetime,
    error,
    setError,
    attachment,
    setAttachment,
  } = useScheduledMessageModalStore()
  const { data: scheduledMessages } = useScheduledMessagesQuery()
  const scheduledMessage = scheduledMessages?.find(
    (message) => message.id === scheduledMessageId
  )
  const { currentClientProfile, currentCoachingGroup } = useCurrentClient()
  const { isClientOfOtherCoach } = useUserRole()
  const { mutateAsync: createScheduledMessage, isLoading: isCreating } =
    useCreateScheduledMessageMutation()
  const { mutateAsync: updateScheduledMessage, isLoading: isUpdating } =
    useUpdateScheduledMessageMutation()

  const { onOpen: openDeleteModal } = useDeleteScheduledMessageModalStore()

  const handleAddScheduledMessage = useThrottle(async () => {
    if (!currentClientProfile?.user.timezone) return
    if (!currentCoachingGroup) return setError("No coaching group selected")
    if (!clientDatetime) return setError("Please select a date and time")
    if (!message) return setError("Please enter a message")

    // Ensure time is in the correct format - compare in client timezone

    const clientDayJSInClientTimezone = clientDatetime.tz(
      currentClientProfile?.user.timezone,
      true
    )
    const timeNow = dayjs().tz(currentClientProfile?.user.timezone)
    const timePlus5Mins = timeNow.add(5, "minutes")
    if (clientDayJSInClientTimezone.isBefore(timePlus5Mins))
      return setError("Time must be at least 5 minutes from now")
    setError("")

    let attachmentId = undefined
    if (attachment) {
      const formData = new FormData()
      formData.append("resource_url", attachment)
      formData.append("description", attachment.type)
      formData.append("title", attachment.name)

      const resource = await createResource(formData)
      attachmentId = resource.id
    }

    await createScheduledMessage({
      groupId: currentCoachingGroup.id,
      message,
      datetime: clientDayJSInClientTimezone.format(),
      resourceId: attachmentId,
    })
    trackAnalyticEvent(ANALYTIC_EVENTS.ADD_SCHEDULED_MESSAGE)
    onClose()
  }, 1000)

  const handleEditScheduledMessage = useThrottle(async () => {
    if (!currentClientProfile?.user.timezone) return
    if (!currentCoachingGroup) return setError("No coaching group selected")
    if (!clientDatetime) return setError("Please select a date and time")
    if (!scheduledMessageId) return setError("No scheduled message id")
    if (!message) return setError("Please enter a message")

    // time also has to be at least 5 mins from now - compare in client timezone
    const clientDayJSInClientTimezone = clientDatetime.tz(
      currentClientProfile?.user.timezone,
      true
    )
    const timeNow = dayjs().tz(currentClientProfile?.user.timezone)
    const timePlus5Mins = timeNow.add(5, "minutes")
    if (clientDayJSInClientTimezone.isBefore(timePlus5Mins))
      return setError("Time must be at least 5 minutes from now")
    setError("")

    let attachmentId: number | null | undefined
    if (attachment) {
      const formData = new FormData()
      formData.append("resource_url", attachment)
      formData.append("description", attachment.type)
      formData.append("title", attachment.name)

      const resource = await createResource(formData)
      attachmentId = resource.id
    } else if (attachment === null) {
      attachmentId = null
    }

    await updateScheduledMessage({
      messageId: scheduledMessageId,
      message,
      datetime: clientDayJSInClientTimezone.format(),
      resourceId: attachmentId,
    })
    onClose()
  }, 1000)

  if (!currentClientProfile?.user.timezone) return

  const setDate = (date: Dayjs | null) => {
    const updatedDatetime = updateDateInDatetime(clientDatetime, date)
    if (updatedDatetime) setClientDatetime(updatedDatetime)
  }

  const setTime = (time: string) => {
    const updatedDatetime = updateTimeInDatetime(clientDatetime, time)
    if (updatedDatetime) setClientDatetime(updatedDatetime)
  }

  const timeValue = clientDatetime.format("HH:mm")

  return (
    <>
      <ModalHeader
        title={
          view === ScheduledMessageModalView.ADD
            ? "Create a scheduled message"
            : "Edit scheduled message"
        }
        onClose={onClose}
        showBorderBottom
      />
      <div className="p-6 pt-4 gap-2 flex flex-col">
        <Typography variant="body">Message:</Typography>
        <CustomTextArea
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder="Enter your message here"
          maxLength={500}
        />
        {attachment ? (
          <div className="flex flex-col gap-1">
            <AttachmentItem
              key={attachment.name}
              name={attachment.name}
              url={URL.createObjectURL(attachment)}
              onRemove={() => setAttachment(undefined)}
            />
          </div>
        ) : attachment === null ? (
          <AddAttachmentButton
            attachments={attachment ? [attachment] : []}
            setAttachments={(files) => setAttachment(files[0])}
          />
        ) : scheduledMessage?.resource ? (
          <AttachmentItem
            name={scheduledMessage.resource.title}
            url={scheduledMessage.resource.resource_url}
            onRemove={() => setAttachment(null)} // delete resource
          />
        ) : (
          <AddAttachmentButton
            attachments={attachment ? [attachment] : []}
            setAttachments={(files) => setAttachment(files[0])}
          />
        )}
        <div className="flex flex-col pt-2">
          <Typography variant="body">Schedule for:</Typography>
          <Typography variant="bodySmall">
            You can schedule for up to 14 days from today
          </Typography>
        </div>
        <div className="flex flex-row gap-2 items-center">
          <DateInput date={clientDatetime} setDate={setDate} />
          <TimeInputNew value={timeValue} setValue={setTime} />
          <Typography variant="bodySmall">(client local time)</Typography>
        </div>
        {error && (
          <div className="flex flex-row w-full p-6 pt-2 pb-0 gap-4 justify-between items-center">
            <Typography
              variant="bodySmall"
              textAlign="center"
              color={JoonUIColor.semantic.destructive}
            >
              {error}
            </Typography>
          </div>
        )}
        <div className="flex flex-row gap-4 justify-between pt-4">
          {view === ScheduledMessageModalView.EDIT && (
            <TextButton onClick={openDeleteModal}>
              <Typography
                variant="body"
                color={JoonUIColor.semantic.destructive}
              >
                Delete
              </Typography>
            </TextButton>
          )}
          <div className="flex flex-row gap-4 items-center justify-end w-full">
            <TextButton onClick={onClose}>
              <Typography variant="body" color={JoonUIColor.text.primaryAccent}>
                Cancel
              </Typography>
            </TextButton>
            <Button
              text={
                view === ScheduledMessageModalView.ADD
                  ? "Schedule message"
                  : "Save changes"
              }
              onClick={
                view === ScheduledMessageModalView.ADD
                  ? handleAddScheduledMessage
                  : handleEditScheduledMessage
              }
              buttonType="primary"
              isLoading={isCreating || isUpdating}
              disabled={isClientOfOtherCoach}
            />
          </div>
        </div>
      </div>
      <DeleteScheduledMessageModal />
    </>
  )
}

export default AddEditScheduledMessage
